import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import axiosInstance from '../../../config/axios.config';
import ReportBanner from '../ReportBanner';
import ConsentCheckbox from './ConsentCheckbox';
import GlibGstReturnOutputComponent from '../output/GlibGstReturnOutputComponent';

const MAX_FILES = 12;
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB per return (matches config/glib.php)
const ACCEPTED = '.pdf,.jpg,.jpeg,.png';

// Poll cadence for step 4. A 12-return analysis took ~2 min live, so 15s
// keeps the UI responsive without hammering GLIB (which rate-limits the status
// endpoint too). Hard cap so a stuck vendor job can never spin forever.
const POLL_MS = 15000;
const MAX_POLLS = 40; // ≈ 10 minutes

const STAGES = { FORM: 0, RUNNING: 1, DONE: 2 };

/**
 * GLIB (Autosift) GST Return Analysis (GSTR-3B) — input component.
 *
 * Unlike every other report in this folder, GLIB is a multi-step ASYNC vendor
 * pipeline, so this cannot use SimpleReportForm / FileUploadReportForm (both are
 * single-POST wrappers). The four backend endpoints are chained behind ONE
 * "Analyse" button so the DSA never has to understand the vendor's stages:
 *
 *   1. POST /glib/gst-return/create-work-order
 *   2. POST /glib/gst-return/{reportId}/upload      (1..12 files)
 *   3. POST /glib/gst-return/{reportId}/process     (billable)
 *   4. GET  /glib/gst-return/{reportId}/status      (polled until complete)
 *
 * Mirrors GlibBankStatementInputComponent: the vendor pipeline is identical,
 * only the report type, upload path and file_type differ.
 */
const GlibGstReturnInputComponent = ({
    serviceId: serviceIdProp,
    serviceDetails: serviceDetailsProp,
    leadId,
    initialReportData,
    onReportChange,
} = {}) => {
    const { serviceId: serviceIdParam } = useParams();
    const serviceId = serviceIdProp || serviceIdParam;

    const toast = useRef(null);
    const pollTimerRef = useRef(null);
    const pollCountRef = useRef(0);
    const cancelledRef = useRef(false);

    const [files, setFiles] = useState([]); // [{ file, password, locked }]
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});

    const [stage, setStage] = useState(initialReportData ? STAGES.DONE : STAGES.FORM);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ pct: 0, note: '' });

    const [reportId, setReportId] = useState(null);
    const [workOrderId, setWorkOrderId] = useState(null);
    const [statusData, setStatusData] = useState(null);
    const [reportData, setReportData] = useState(initialReportData || null);
    const [failedUploads, setFailedUploads] = useState([]);
    const [fatalError, setFatalError] = useState('');

    const [serviceDetails, setServiceDetails] = useState(serviceDetailsProp || null);
    const [fetchingService, setFetchingService] = useState(true);

    // Always fetch fresh credit cost on mount (see CLAUDE.md "Credits Required").
    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        let alive = true;
        const load = async () => {
            setFetchingService(true);
            try {
                const res = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (alive && res.data.success && res.data.data) setServiceDetails(res.data.data);
            } catch (error) {
                console.error('Load service details failed:', error?.message || 'Unknown error');
                if (alive) setServiceDetails((prev) => prev || { available_credits: 'N/A', credits_required: 'N/A' });
            } finally {
                if (alive) setFetchingService(false);
            }
        };
        load();
        return () => { alive = false; };
    }, [serviceId]);

    // Stop polling if the component unmounts mid-analysis (drawer closed, route
    // change) — otherwise the interval keeps firing against a dead component.
    useEffect(() => () => {
        cancelledRef.current = true;
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    }, []);

    /* ------------------------------------------------------------------ files */

    const addFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;

        setFiles((prev) => {
            const room = MAX_FILES - prev.length;
            if (room <= 0) {
                toast.current?.show({ severity: 'warn', summary: 'Limit reached', detail: `Maximum ${MAX_FILES} returns`, life: 3000 });
                return prev;
            }
            const accepted = picked.slice(0, room).map((file) => ({ file, password: '', locked: false }));
            if (picked.length > room) {
                toast.current?.show({ severity: 'warn', summary: 'Some files skipped', detail: `Only ${room} more allowed (max ${MAX_FILES})`, life: 3500 });
            }
            return [...prev, ...accepted];
        });
        // Allow re-picking the same file after a removal.
        e.target.value = '';
        setErrors((prev) => ({ ...prev, files: '' }));
    };

    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
    const clearFiles = () => setFiles([]);

    const patchFile = (idx, patch) =>
        setFiles((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

    const totalBytes = useMemo(() => files.reduce((a, f) => a + (f.file?.size || 0), 0), [files]);

    const validate = () => {
        const next = {};
        if (files.length === 0) next.files = 'Add at least one GST return';
        else {
            const tooBig = files.find((f) => f.file.size > MAX_BYTES);
            if (tooBig) next.files = `${tooBig.file.name} exceeds 20 MB`;
        }
        if (!consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    /* --------------------------------------------------------------- pipeline */

    const finishWithReport = useCallback((payload) => {
        setReportData(payload);
        onReportChange?.(payload);
        setStage(STAGES.DONE);
        setBusy(false);
    }, [onReportChange]);

    // Step 4 — poll until the vendor finishes. Self-rescheduling timeout rather
    // than setInterval so a slow response can never overlap the next poll.
    const poll = useCallback(async (rid) => {
        if (cancelledRef.current) return;

        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLLS) {
            setBusy(false);
            setFatalError('The analysis is taking longer than expected. It is still running at the provider — check Report History in a few minutes.');
            return;
        }

        try {
            const res = await axiosInstance.get(`/glib/gst-return/${rid}/status`);
            const d = res.data || {};
            setStatusData(d);

            if (d.is_complete) {
                setProgress({ pct: 100, note: 'Analysis complete' });
                finishWithReport(d);
                toast.current?.show({ severity: 'success', summary: 'Analysis complete', detail: `${d.returns_analysed ?? files.length} return(s) analysed`, life: 3000 });
                return;
            }

            if (d.is_failed) {
                setBusy(false);
                // The backend already translates provider errors into tenant-safe
                // wording (e.g. GLIB's own "Insufficient credits" must not read as
                // if the tenant's wallet is empty). Append the refund note and the
                // support reference when present.
                const parts = [d.message || 'The provider could not analyse these returns.'];
                if (d.credits_refunded > 0) {
                    parts.push(`${d.credits_refunded} credit(s) have been refunded to your wallet.`);
                }
                if (d.vendor_error_code) {
                    parts.push(`Provider reference: ${d.vendor_error_code}`);
                }
                setFatalError(parts.join(' '));
                return;
            }

            // Still processing (including a rate-limited status check, which the
            // backend reports as is_processing with a retry_after hint).
            const waited = Math.min(90, 30 + pollCountRef.current * 2);
            setProgress({ pct: waited, note: d.rate_limited ? 'Provider is busy — retrying shortly…' : 'Analysing returns at the provider…' });
            const delay = (d.retry_after ? d.retry_after * 1000 : POLL_MS);
            pollTimerRef.current = setTimeout(() => poll(rid), delay);
        } catch (error) {
            console.error('GLIB status poll failed:', error?.message || 'Unknown error');
            // Transient network blip — keep polling rather than failing the run.
            pollTimerRef.current = setTimeout(() => poll(rid), POLL_MS);
        }
    }, [files.length, finishWithReport]);

    // Steps 2 + 3, reused by "Analyse anyway" / "Retry" after a partial upload.
    const uploadAndProcess = useCallback(async (rid, list, { acceptPartial = false } = {}) => {
        // Step 2 — upload
        setProgress({ pct: 15, note: `Uploading ${list.length} return(s)…` });
        const fd = new FormData();
        list.forEach((entry, i) => {
            fd.append('files[]', entry.file);
            if (entry.locked && entry.password) fd.append(`passwords[${i}]`, entry.password);
        });

        const up = await axiosInstance.post(`/glib/gst-return/${rid}/upload`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        const upData = up.data || {};
        const failed = upData.failed || [];
        setFailedUploads(failed);

        if ((upData.uploaded_count || 0) === 0) {
            setBusy(false);
            setFatalError(upData.message || 'None of the returns could be uploaded. No credits were charged.');
            return;
        }

        setProgress({ pct: 45, note: `${upData.uploaded_count} uploaded — starting analysis…` });

        // Step 3 — process (billable). A 409 means some files failed and the
        // backend is refusing to bill for a partial analysis until we opt in.
        try {
            const pr = await axiosInstance.post(
                `/glib/gst-return/${rid}/process`,
                acceptPartial ? { accept_partial: true } : {},
            );
            const prData = pr.data || {};
            if (!prData.success) throw new Error(prData.message || 'Could not start the analysis');

            setProgress({ pct: 60, note: 'Analysis started — waiting for the provider…' });
            pollCountRef.current = 0;
            poll(rid);
        } catch (error) {
            const resp = error.response?.data;
            if (error.response?.status === 409 && resp?.code === 'INCOMPLETE_UPLOAD') {
                // Surface the guard: let the user retry the missing files or proceed.
                setBusy(false);
                setFailedUploads(
                    (resp.failed_files || []).map((name) => ({ file_name: name, error: 'Upload failed' })),
                );
                setFatalError('');
                setProgress({ pct: 0, note: '' });
                setStatusData({ incomplete: true, ...resp });
                return;
            }
            throw error;
        }
    }, [poll]);

    // Step 1 → 3, chained.
    const runPipeline = useCallback(async () => {
        setBusy(true);
        setFatalError('');
        setFailedUploads([]);
        setStatusData(null);
        setStage(STAGES.RUNNING);
        setProgress({ pct: 5, note: 'Creating work order…' });

        try {
            const body = { api_provider_service_id: parseInt(serviceDetails?.id || serviceId, 10) };
            if (leadId) body.lead_id = leadId;

            const create = await axiosInstance.post('/glib/gst-return/create-work-order', body);
            const cd = create.data || {};
            if (!cd.success || !cd.report_id) throw new Error(cd.message || 'Could not create the work order');

            setReportId(cd.report_id);
            setWorkOrderId(cd.work_order_id);

            await uploadAndProcess(cd.report_id, files);
        } catch (error) {
            console.error('GLIB analysis failed:', error?.message || 'Unknown error');
            setBusy(false);
            const msg = error.response?.data?.message || error?.message || 'Analysis failed';
            setFatalError(msg);
            toast.current?.show({ severity: 'error', summary: 'Analysis failed', detail: msg, life: 4000 });
        }
    }, [serviceId, serviceDetails, leadId, files, uploadAndProcess]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});

        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }

        confirmDialog({
            group: 'glib-gst-confirm',
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">GST returns:</span> <span className="text-900 font-medium">{files.length} file{files.length !== 1 ? 's' : ''}</span></div>
                    <div className="mb-2"><span className="text-600">Total size:</span> <span className="text-900 font-medium">{(totalBytes / 1024 / 1024).toFixed(1)} MB</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div className="mb-2"><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                    <div className="text-600 text-xs mt-2">Analysis takes about 2 minutes. Keep this page open.</div>
                </div>
            ),
            header: 'Confirm GST return analysis',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, analyse',
            rejectLabel: 'Cancel',
            accept: runPipeline,
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        cancelledRef.current = false;
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        setReportData(null);
        onReportChange?.(null);
        setFiles([]);
        setConsent(false);
        setErrors({});
        setStage(STAGES.FORM);
        setBusy(false);
        setProgress({ pct: 0, note: '' });
        setReportId(null);
        setWorkOrderId(null);
        setStatusData(null);
        setFailedUploads([]);
        setFatalError('');
    };

    const retryFailedOnly = () => {
        // Re-upload just the files that failed, onto the SAME work order.
        const failedNames = new Set(failedUploads.map((f) => f.file_name));
        const retryList = files.filter((f) => failedNames.has(f.file.name));
        if (retryList.length === 0 || !reportId) return;
        setBusy(true);
        setStage(STAGES.RUNNING);
        uploadAndProcess(reportId, retryList).catch((error) => {
            console.error('GLIB retry failed:', error?.message || 'Unknown error');
            setBusy(false);
            setFatalError(error.response?.data?.message || 'Retry failed');
        });
    };

    // Opt in to analysing only the files that uploaded successfully. The files
    // are already on the work order, so this calls /process directly — going
    // back through uploadAndProcess would re-upload and 422 on an empty list.
    const proceedPartial = async () => {
        if (!reportId) return;
        setBusy(true);
        setStage(STAGES.RUNNING);
        setStatusData(null);
        setProgress({ pct: 50, note: 'Starting analysis…' });

        try {
            const pr = await axiosInstance.post(
                `/glib/gst-return/${reportId}/process`,
                { accept_partial: true },
            );
            if (!pr.data?.success) throw new Error(pr.data?.message || 'Could not start the analysis');
            setProgress({ pct: 60, note: 'Analysis started — waiting for the provider…' });
            pollCountRef.current = 0;
            poll(reportId);
        } catch (error) {
            console.error('GLIB partial process failed:', error?.message || 'Unknown error');
            setBusy(false);
            setFatalError(error.response?.data?.message || 'Could not start the analysis');
        }
    };

    const fieldErr = (key, helpId) => errors[key] && (
        <small id={helpId} className="p-error block mt-1" role="alert">
            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{errors[key]}
        </small>
    );

    /* ----------------------------------------------------------------- render */

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog group="glib-gst-confirm" />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-percentage"
                        title="GST Return Analysis"
                        subtitle="Upload up to 12 monthly GSTR-3B returns for turnover, tax and ITC analysis"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />

                    {/* Input form — stays mounted while the analysis runs so the
                        user keeps the context of what they submitted. Replaced by
                        the report once the analysis completes. */}
                    {stage !== STAGES.DONE && (
                        <Card pt={{ root: { className: 'border-1 surface-200 border-left-3 border-green-500 surface-0' } }}>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-file-pdf text-primary" aria-hidden="true" />
                                    <span className="text-sm font-semibold text-900 uppercase">GST Returns (GSTR-3B)</span>
                                </div>

                                <p className="text-600 text-sm mt-0 mb-3">
                                    PDF, JPG or PNG. Up to {MAX_FILES} monthly returns, 20 MB each.
                                </p>

                                <div className="field col-12 p-0 mb-3">
                                    <label htmlFor="glib-files" className="block font-medium text-sm mb-2">
                                        Select returns <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="glib-files"
                                        type="file"
                                        accept={ACCEPTED}
                                        multiple
                                        disabled={busy || files.length >= MAX_FILES}
                                        onChange={addFiles}
                                        className="w-full"
                                        aria-invalid={!!errors.files}
                                        aria-describedby="glib-files-help"
                                    />
                                    {fieldErr('files', 'glib-files-help')}
                                </div>

                                {files.length > 0 && (
                                    <>
                                        <div className="flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                            <Tag
                                                severity={files.length >= MAX_FILES ? 'warning' : 'info'}
                                                icon="pi pi-paperclip"
                                                value={`${files.length} / ${MAX_FILES} selected · ${(totalBytes / 1024 / 1024).toFixed(1)} MB`}
                                                className="text-sm"
                                            />
                                            <Button
                                                type="button"
                                                label="Clear all"
                                                icon="pi pi-times"
                                                severity="secondary"
                                                outlined
                                                size="small"
                                                disabled={busy}
                                                onClick={clearFiles}
                                            />
                                        </div>

                                        <div className="flex flex-column gap-2 mb-3">
                                            {files.map((entry, idx) => (
                                                <div key={`${entry.file.name}-${idx}`} className="surface-100 border-round p-2">
                                                    <div className="flex align-items-center gap-2">
                                                        <i className="pi pi-file-pdf text-primary flex-shrink-0" aria-hidden="true" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-900 text-sm font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                                {entry.file.name}
                                                            </div>
                                                            <div className="text-500 text-xs">
                                                                {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                                                            </div>
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            icon={entry.locked ? 'pi pi-lock' : 'pi pi-lock-open'}
                                                            severity={entry.locked ? 'warning' : 'secondary'}
                                                            text
                                                            rounded
                                                            size="small"
                                                            disabled={busy}
                                                            aria-label={entry.locked ? 'Remove password' : 'This PDF is password protected'}
                                                            tooltip={entry.locked ? 'Password protected' : 'Add password'}
                                                            tooltipOptions={{ position: 'top' }}
                                                            onClick={() => patchFile(idx, { locked: !entry.locked, password: '' })}
                                                        />
                                                        <Button
                                                            type="button"
                                                            icon="pi pi-trash"
                                                            severity="danger"
                                                            text
                                                            rounded
                                                            size="small"
                                                            disabled={busy}
                                                            aria-label={`Remove ${entry.file.name}`}
                                                            onClick={() => removeFile(idx)}
                                                        />
                                                    </div>
                                                    {entry.locked && (
                                                        <div className="mt-2">
                                                            <IconField iconPosition="left" className="w-full">
                                                                <InputIcon className="pi pi-key" />
                                                                <InputText
                                                                    value={entry.password}
                                                                    onChange={(e) => patchFile(idx, { password: e.target.value })}
                                                                    placeholder="PDF password"
                                                                    type="password"
                                                                    maxLength={60}
                                                                    spellCheck={false}
                                                                    disabled={busy}
                                                                    className="w-full p-inputtext-sm"
                                                                    aria-label={`Password for ${entry.file.name}`}
                                                                />
                                                            </IconField>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                <Divider />
                                <div className="grid">
                                    <ConsentCheckbox
                                        checked={consent}
                                        onChange={(e) => { setConsent(e.checked); setErrors((p) => ({ ...p, consent: '' })); }}
                                        error={errors.consent}
                                        reportType="gstin"
                                    />
                                </div>

                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Analyse GST Returns"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={busy}
                                        disabled={fetchingService}
                                        className="w-full md:w-auto"
                                    />
                                </div>
                            </form>
                        </Card>
                    )}

                    {/* Inline status, directly under the input — no separate step
                        screen. Processing, the partial-upload gate and errors all
                        render here so the user never leaves the one page. */}
                    {stage === STAGES.RUNNING && (
                        <Card className="mt-3">
                            {statusData?.incomplete ? (
                                <>
                                    <Message
                                        severity="warn"
                                        className="w-full mb-3"
                                        content={
                                            <div className="text-sm">
                                                <div className="font-medium mb-1">
                                                    {statusData.failed_count} of {(statusData.uploaded_count || 0) + (statusData.failed_count || 0)} return(s) failed to upload
                                                </div>
                                                <div className="text-700">No credits have been charged yet.</div>
                                            </div>
                                        }
                                    />
                                    <div className="flex flex-column gap-1 mb-3">
                                        {failedUploads.map((f, i) => (
                                            <div key={`${f.file_name}-${i}`} className="flex align-items-center gap-2 text-sm">
                                                <i className="pi pi-times-circle text-red-500" aria-hidden="true" />
                                                <span className="text-900">{f.file_name}</span>
                                                <span className="text-500 text-xs">{f.error}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex flex-column md:flex-row gap-2">
                                        <Button label="Retry failed files" icon="pi pi-refresh" onClick={retryFailedOnly} className="flex-1" />
                                        <Button
                                            label={`Analyse the ${statusData.uploaded_count} that succeeded`}
                                            icon="pi pi-check"
                                            severity="secondary"
                                            outlined
                                            onClick={proceedPartial}
                                            className="flex-1"
                                        />
                                    </div>
                                    <Button label="Start over" icon="pi pi-arrow-left" text size="small" className="mt-2" onClick={handleAnother} />
                                </>
                            ) : fatalError ? (
                                <>
                                    <Message severity="error" text={fatalError} className="w-full mb-3" />
                                    <Button label="Start over" icon="pi pi-refresh" onClick={handleAnother} />
                                </>
                            ) : (
                                <div className="flex align-items-center gap-3">
                                    <i className="pi pi-spin pi-spinner text-primary pi-2x flex-shrink-0" aria-hidden="true" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-900 text-sm font-semibold mb-1">
                                            Processing — {progress.note || 'working…'}
                                        </div>
                                        <ProgressBar
                                            value={progress.pct}
                                            showValue={false}
                                            style={{ height: '0.375rem' }}
                                            aria-label="Analysis progress"
                                        />
                                        <div className="text-500 text-xs mt-2">
                                            Usually takes about 2 minutes — keep this page open.
                                            {workOrderId && (
                                                <> · Ref <span className="font-mono">{workOrderId.slice(0, 8)}</span></>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}

                    {/* Results — expand below, exactly where the form was. */}
                    {stage === STAGES.DONE && (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Submit another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <GlibGstReturnOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default GlibGstReturnInputComponent;
